import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import type { Map as MapLibreMap } from "maplibre-gl";
import { Button, cn } from "@wondermaps/ui";
import { Sentry } from "../../lib/sentry";
import {
  MapCanvas,
  type POI,
  type Layer,
  type MapCanvasRef,
  type MapLabel,
  type UserLocation,
  type VisitorIllustration,
} from "../MapCanvas";
import { MapErrorFallback } from "../ErrorFallback";
import { RecenterButton } from "./RecenterButton";
import { MockLocationPanel } from "../debug/MockLocationPanel";
import type { ThemeConfig, DynamicCategory } from "@wondermaps/maps";

interface MapSectionProps {
  id?: string;
  className?: string;
  layers: Layer[];
  pois: POI[];
  labels: MapLabel[];
  categories?: DynamicCategory[];
  defaultCenter?: [number, number];
  defaultZoom?: number;
  minZoom?: number;
  maxZoom?: number;
  zoomOffset?: number;
  onPOIClick: (poi: POI) => void;
  themeConfig?: ThemeConfig;
  userLocation?: UserLocation;
  selectedCategory: string | null | undefined;
  onClearCategory: () => void;
  onRecenter: () => void;
  onLocationRequest: () => void;
  mapInstance: MapLibreMap | null;
  onContextLost?: () => void;
  /** For lazy-loading shadow layers */
  orgSlug?: string;
  mapSlug?: string;
  /** Highlight selected POI with ring + always-visible icon */
  selectedPoiId?: string | null;
  /** Custom map sticker placements (plan 120, PR5). */
  illustrations?: VisitorIllustration[];
}

/**
 * Map section for visitor view - contains MapCanvas and overlay controls
 * (empty state, POI count badge, recenter button, mock location panel)
 */
export const MapSection = forwardRef<MapCanvasRef, MapSectionProps>(function MapSection(
  {
    id,
    className,
    layers,
    pois,
    labels,
    categories,
    defaultCenter,
    defaultZoom,
    minZoom,
    maxZoom,
    zoomOffset,
    onPOIClick,
    themeConfig,
    userLocation,
    selectedCategory,
    onClearCategory,
    onRecenter,
    onLocationRequest,
    mapInstance,
    onContextLost,
    orgSlug,
    mapSlug,
    selectedPoiId,
    illustrations,
  },
  ref
) {
  const { t } = useTranslation("map");

  return (
    <main id={id} className={cn("relative", className)}>
      <Sentry.ErrorBoundary fallback={MapErrorFallback}>
        <MapCanvas
          ref={ref}
          layers={layers}
          pois={pois}
          labels={labels}
          categories={categories}
          defaultCenter={defaultCenter}
          defaultZoom={defaultZoom}
          minZoom={minZoom}
          maxZoom={maxZoom}
          zoomOffset={zoomOffset}
          onPOIClick={onPOIClick}
          interactive={true}
          themeConfig={themeConfig}
          userLocation={userLocation}
          onContextLost={onContextLost}
          selectedCategory={selectedCategory}
          orgSlug={orgSlug}
          mapSlug={mapSlug}
          selectedPoiId={selectedPoiId}
          illustrations={illustrations}
        />
      </Sentry.ErrorBoundary>

      {/* Empty state */}
      {pois.length === 0 && selectedCategory !== "all" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="rounded-lg bg-background/90 px-4 py-3 text-center shadow-lg backdrop-blur pointer-events-auto">
            <p className="text-sm text-muted-foreground">
              {t("category.emptyCategory")}
            </p>
            <Button
              variant="link"
              size="sm"
              className="mt-1 h-auto p-0"
              onClick={onClearCategory}
            >
              {t("category.showAllAction")}
            </Button>
          </div>
        </div>
      )}

      {/* POI Count Badge - with safe area offset */}
      <div className="absolute bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] left-4 rounded-full bg-background/90 px-3 py-1 text-sm shadow-lg backdrop-blur">
        {t("list.poiCount", { count: pois.length })}
      </div>

      {/* Location Button - gray when not enabled, blue when enabled */}
      <RecenterButton
        hasLocation={!!userLocation}
        onClick={userLocation ? onRecenter : onLocationRequest}
      />

      {/* Mock Location Panel (debug mode only) */}
      <MockLocationPanel map={mapInstance} />
    </main>
  );
});
